/**
 * Flowlytics Pixel SDK - Event Queue System
 * Batches events and sends them efficiently
 */

class EventQueue {
  constructor(config) {
    this.config = config;
    this.queue = [];
    this.timer = null;
    this.isSending = false;
    this.isUnloading = false; // Track if page is unloading
    this.MAX_EVENT_AGE_MS = 5 * 60 * 1000; // 5 minutes max age for events
    this.MAX_RETRIES = 3;
    // Pre-signed beacon payload: sendBeacon cannot set headers, so the HMAC
    // travels as ?sig= and must be computed BEFORE the page starts unloading.
    this._presigned = null; // { body, sig }

    // localStorage mirror: recover events that were queued but never sent
    // because the page died without a graceful unload (crash, OOM tab kill,
    // force-quit) — the visibilitychange/pagehide beacon handlers cover the
    // normal navigation case, this covers the abnormal one. Scoped by apiKey
    // prefix so multiple installations on one site don't clobber each other.
    this._storageKey = `flowlytics_pending_${(config.apiKey || "").slice(0, 16)}`;
    this.restoreFromStorage();

    // Setup page unload handlers
    this.setupUnloadHandlers();
  }

  /**
   * Best-effort localStorage read/write — never let storage errors (quota,
   * privacy mode, disabled storage, cross-origin iframe) affect tracking.
   */
  persistQueue() {
    try {
      if (typeof localStorage === "undefined") return;
      if (this.queue.length === 0) {
        localStorage.removeItem(this._storageKey);
      } else {
        localStorage.setItem(this._storageKey, JSON.stringify(this.queue));
      }
    } catch (error) {
      // Storage unavailable or full — tracking continues in-memory only.
    }
  }

  /**
   * Recover events left behind by a page that never unloaded gracefully.
   * Only valid (non-expired) events are kept; the rest are dropped since
   * MAX_EVENT_AGE_MS has already made them too stale to attribute reliably.
   * Recovered events go through the normal flush path, so they're still
   * subject to server-side event_id dedup if the original send actually
   * did land — an occasional harmless re-send is the acceptable tradeoff
   * for not silently losing data on abnormal termination.
   */
  restoreFromStorage() {
    try {
      if (typeof localStorage === "undefined") return;
      const raw = localStorage.getItem(this._storageKey);
      if (!raw) return;
      const stored = JSON.parse(raw);
      if (!Array.isArray(stored) || stored.length === 0) {
        localStorage.removeItem(this._storageKey);
        return;
      }
      const valid = stored.filter((event) => this.isEventValid(event));
      if (valid.length > 0) {
        this.queue = valid;
        this.scheduleBatch();
      }
      this.persistQueue();
    } catch (error) {
      // Corrupted/unreadable stored payload — start with an empty queue.
    }
  }

  /**
   * Generate a unique event_id (UUID v4).
   * Shared end-to-end: server dedup, CAPI and browser-pixel dedup (fbq eventID)
   * all key off this value.
   */
  generateEventId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for older browsers
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Setup handlers for page unload to send events with sendBeacon
   */
  setupUnloadHandlers() {
    if (typeof window === "undefined") return;

    // Use visibilitychange as primary (more reliable)
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.flushWithBeacon();
      }
    });

    // Fallback for older browsers
    window.addEventListener("pagehide", () => {
      this.flushWithBeacon();
    });

    // Mark as unloading to suppress errors
    window.addEventListener("beforeunload", () => {
      this.isUnloading = true;
    });
  }

  /**
   * Build the beacon payload body for the current queue.
   * Deterministic (no sent_at): the same queue always serializes to the same
   * string, so a signature computed at add() time is still valid at unload.
   */
  buildBeaconBody() {
    return JSON.stringify({
      api_key: this.config.apiKey,
      events: this.queue,
      sdk_version: __SDK_VERSION__,
    });
  }

  /**
   * Pre-compute the HMAC for the current queue so flushWithBeacon() can send
   * a signed payload synchronously (sendBeacon cannot set headers and the
   * page may die before an async sign completes at unload time).
   */
  preSignBeaconPayload() {
    if (!this.config.secretKey || this.queue.length === 0) return;

    const body = this.buildBeaconBody();
    this.generateHmacSignatureForString(body)
      .then((sig) => {
        if (sig) this._presigned = { body, sig };
      })
      .catch(() => {});
  }

  /**
   * Flush queue using sendBeacon (for page unload)
   * sendBeacon is fire-and-forget, guaranteed to send even during navigation
   */
  flushWithBeacon() {
    if (this.queue.length === 0) return;

    this.cleanExpiredEvents();
    if (this.queue.length === 0) return;

    const body = this.buildBeaconBody();
    let endpoint = this.config.endpoint;

    if (this.config.secretKey) {
      if (this._presigned && this._presigned.body === body) {
        endpoint += `${endpoint.includes("?") ? "&" : "?"}sig=${this._presigned.sig}`;
      } else {
        // Snapshot is stale (events added after the last pre-sign finished).
        // Sign asynchronously and send if the page is still alive; the queue
        // is kept so the fetch-flush path can still deliver on resume.
        this.generateHmacSignatureForString(body)
          .then((sig) => {
            if (!sig) return;
            const url = `${this.config.endpoint}${this.config.endpoint.includes("?") ? "&" : "?"}sig=${sig}`;
            const lateBlob = new Blob([body], { type: "application/json" });
            if (navigator.sendBeacon(url, lateBlob)) {
              this.queue = [];
              this.persistQueue();
            }
          })
          .catch(() => {});
        return;
      }
    }

    // sendBeacon is synchronous and guaranteed to complete
    const blob = new Blob([body], { type: "application/json" });
    const sent = navigator.sendBeacon(endpoint, blob);

    if (sent) {
      this.queue = [];
      this.persistQueue();
      this._presigned = null;
      if (this.config.debug) {
        console.log("[Flowlytics Queue] Sent events via sendBeacon");
      }
    }
  }

  /**
   * Add event to queue
   */
  add(event) {
    this.queue.push({
      ...event,
      event_id: event.event_id || this.generateEventId(),
      timestamp: event.timestamp || new Date().toISOString(),
      queued_at: Date.now(),
    });
    this.persistQueue();

    // Keep a signed snapshot ready for the beacon path (fire-and-forget)
    this.preSignBeaconPayload();

    if (this.config.debug) {
      console.log("[Flowlytics Queue] Event added:", event.event_type);
    }

    // Send immediately if batch size reached
    if (this.queue.length >= this.config.batchSize) {
      this.flush();
    } else {
      // Schedule batch send
      this.scheduleBatch();
    }
  }

  /**
   * Schedule batch send
   */
  scheduleBatch() {
    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.timer = setTimeout(() => {
      this.flush();
    }, this.config.batchInterval);
  }

  /**
   * Check if an event is still valid (not expired)
   */
  isEventValid(event) {
    const now = Date.now();
    const eventAge = now - (event.queued_at || now);
    return eventAge < this.MAX_EVENT_AGE_MS;
  }

  /**
   * Clean expired events from queue
   */
  cleanExpiredEvents() {
    const beforeCount = this.queue.length;
    this.queue = this.queue.filter((event) => this.isEventValid(event));
    const removed = beforeCount - this.queue.length;
    if (removed > 0) this.persistQueue();

    if (removed > 0 && this.config.debug) {
      console.log(`[Flowlytics Queue] Cleaned ${removed} expired events`);
    }
  }

  /**
   * Check if error is caused by page navigation (should be silenced)
   */
  isNavigationError(error) {
    if (!error || !error.message) return false;
    const msg = error.message.toLowerCase();
    // These errors occur when fetch is aborted due to navigation
    return (
      msg.includes("failed to fetch") ||
      msg.includes("aborted") ||
      msg.includes("network request failed") ||
      msg.includes("cancelled")
    );
  }

  /**
   * Check if error is permanent (should not retry)
   */
  isPermanentError(error) {
    if (!error.message) return false;
    // 429 (rate limited) and 408 (timeout) are transient — retry them
    if (
      error.message.includes("HTTP 429") ||
      error.message.includes("HTTP 408")
    ) {
      return false;
    }
    // Other 4xx errors are permanent - don't retry
    return error.message.includes("HTTP 4");
  }

  /**
   * Send all queued events
   */
  async flush() {
    if (this.isSending || this.queue.length === 0) {
      return;
    }

    // Clean expired events before sending
    this.cleanExpiredEvents();

    if (this.queue.length === 0) {
      return;
    }

    this.isSending = true;
    const eventsToSend = [...this.queue];
    this.queue = [];
    this.persistQueue();

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    try {
      await this.sendBatch(eventsToSend);

      if (this.config.debug) {
        console.log(`[Flowlytics Queue] Sent ${eventsToSend.length} events`);
      }
    } catch (error) {
      // Suppress errors during page unload - this is expected behavior
      // The visibilitychange/pagehide handlers use sendBeacon as backup
      if (this.isUnloading || this.isNavigationError(error)) {
        if (this.config.debug) {
          console.log(
            "[Flowlytics Queue] Request cancelled during navigation (expected)",
          );
        }
        // Don't retry - sendBeacon handler will take care of it
        return;
      }

      console.error("[Flowlytics Queue] Send failed:", error);

      // Only retry if NOT a permanent error and event is still valid
      if (!this.isPermanentError(error)) {
        eventsToSend.forEach((event) => {
          const retries = event._retries || 0;
          // Only retry if under max retries AND event is still valid
          if (retries < this.MAX_RETRIES && this.isEventValid(event)) {
            this.queue.push({
              ...event,
              _retries: retries + 1,
            });
          } else if (this.config.debug) {
            console.log(
              `[Flowlytics Queue] Dropping event: ${event.event_type} (retries: ${retries}, valid: ${this.isEventValid(event)})`,
            );
          }
        });
        this.persistQueue();
        // Without this, requeued events sit until the NEXT add() call —
        // on a quiet page that means they expire unsent.
        if (this.queue.length > 0) {
          this.preSignBeaconPayload();
          this.scheduleBatch();
        }
      } else {
        // Permanent error - don't retry any events
        if (this.config.debug) {
          console.log(
            `[Flowlytics Queue] Permanent error - dropping ${eventsToSend.length} events`,
          );
        }
      }
    } finally {
      this.isSending = false;
    }
  }

  /**
   * Generate HMAC-SHA256 signature for request payload
   * Prevents tampering and man-in-the-middle attacks
   */
  async generateHmacSignature(payload) {
    return this.generateHmacSignatureForString(JSON.stringify(payload));
  }

  /**
   * HMAC-SHA256 over an already-serialized payload string.
   * The exact same string MUST be sent as the request body: the server
   * verifies against the raw bytes it receives.
   */
  async generateHmacSignatureForString(payloadString) {
    // Only generate signature if secret key is provided
    if (!this.config.secretKey) {
      return null;
    }

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(payloadString);

      // Import secret key
      const key = await crypto.subtle.importKey(
        "raw",
        encoder.encode(this.config.secretKey),
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"],
      );

      // Generate signature
      const signature = await crypto.subtle.sign("HMAC", key, data);

      // Convert to hex string
      return Array.from(new Uint8Array(signature))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    } catch (error) {
      console.error("[Flowlytics] HMAC signature generation failed:", error);
      return null;
    }
  }

  /**
   * Send batch to backend
   */
  async sendBatch(events) {
    const payload = {
      api_key: this.config.apiKey,
      events: events,
      sdk_version: __SDK_VERSION__,
      sent_at: new Date().toISOString(),
    };

    // Generate HMAC signature if secret key is configured
    const signature = await this.generateHmacSignature(payload);

    const headers = {
      "Content-Type": "application/json",
    };

    // Add signature header if available
    if (signature) {
      headers["X-Flowlytics-Signature"] = signature;

      if (this.config.debug) {
        console.log(
          "[Flowlytics] Request signed with HMAC:",
          signature.substring(0, 16) + "...",
        );
      }
    }

    const response = await fetch(this.config.endpoint, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(payload),
      keepalive: true, // Important: send even if page is closing
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get queue size
   */
  size() {
    return this.queue.length;
  }

  /**
   * Clear queue
   */
  clear() {
    this.queue = [];
    this.persistQueue();
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

export default EventQueue;
